import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import Table from '../Components/Tables';

import { useAlert } from '../Components/AlertContext';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import { TaskScheduleApiProvider, useTaskScheduleApi } from '../Services/TaskScheduleApiProvider';
import { TaskScheduleListApi } from '../Services/TaskScheduleApiProviderTypes';
import { taskScheduleColumns } from './TaskScheduleHelpers';

function TaskScheduleContent({ canEdit }) {
  const navigate = useNavigate();
  const { organizationId } = useParams();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();

  const { getTaskSchedules, deleteTaskSchedule } = useTaskScheduleApi();

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<null | string>(null);
  const [taskSchedules, setTaskSchedules] = useState<any[]>([]);
  const [totalTaskSchedules, setTotalTaskSchedules] = useState<number>(0);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);

  function onDelete(selectedId: string[]): void {
    if (!selectedId[0]) return;

    showConfirmation(`Are you sure you want to delete the selected item?`, async () => {
      try {
        await deleteTaskSchedule(selectedId[0]);
        showAlert('success', 'Item was successfully deleted!');
        fetchTaskSchedules(0, itemsPerPage);
      } catch (error) {
        showAlert('fail', 'Failed to delete the item!');
      }
    });
  }

  function onOpen(selectedId: string): void {
    if (!selectedId) return;
    navigate(`/organizations/${organizationId}/task-schedule/${selectedId}`, {
      state: { organizationId },
    });
  }

  const fetchTaskSchedules = async (skip: number, limit: number): Promise<void> => {
    if (limit !== itemsPerPage) {
      setItemsPerPage(limit);
    }
    try {
      setLoading(true);
      const response: TaskScheduleListApi = await getTaskSchedules(skip, limit);
      setTaskSchedules(response.items);
      setTotalTaskSchedules(response.total);
    } catch (error) {
      setError(error.message || 'Failed to fetch task schedules!');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTaskSchedules(0, itemsPerPage);
  }, []);

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-12">
          {error ? (
            <div>Error: {error}</div>
          ) : (
            <>
              <Table
                title="Task Schedules"
                columns={taskScheduleColumns}
                data={taskSchedules}
                selectableRows
                selectableRowsHighlight
                progressPending={loading}
                paginationPerPage={itemsPerPage}
                paginationTotalRows={totalTaskSchedules}
                onRowClicked={(row: any) => onOpen(row.id)}
                sortIcon={<i className="fa-solid fa-chevron-down"></i>}
                onDelete={onDelete}
                onChangePage={fetchTaskSchedules}
              />
              {canEdit && (
                <Button
                  background="color2"
                  size="large"
                  onClick={() =>
                    navigate(`/organizations/${organizationId}/task-schedule/new-task-schedule`)
                  }
                >
                  Create New Task Schedule
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function TaskSchedule({ canEdit }) {
  return (
    <ConfirmationProvider>
      <TaskScheduleApiProvider>
        <TaskScheduleContent canEdit={canEdit} />
      </TaskScheduleApiProvider>
    </ConfirmationProvider>
  );
}

export default TaskSchedule;
